import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * UserMenu component - displays user avatar (if logged in) or login button (if not)
 * Includes settings gear overlay for authenticated users
 */
const UserMenu = ({ onSettingsClick, onLoginClick }) => {
  const { user, isAuthenticated, activeSub, logout, login } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const isMobile = window.innerWidth <= 768;

  // Extract user info from the user object
  const displayName = user?.username || user?.email || user?.name || 'User';
  const userInitial = displayName.charAt(0).toUpperCase();

  // Green: Paid tier with active or trialing status
  const hasActiveSub = activeSub?.tier === 'paid' && (activeSub?.status === 'active' || activeSub?.status === 'trialing');

  // Orange: Paid tier with past_due status (payment failed)
  const isPastDue = activeSub?.tier === 'paid' && activeSub?.status === 'past_due';

  // Black: No subscription, trial tier, or free tier
  const isTrialOrNone = !activeSub || activeSub?.tier === 'trial' || activeSub?.tier === 'free';

  console.log('UserMenu Debug:', {
    activeSub,
    hasActiveSub,
    isTrialOrNone
  });

  // If not authenticated, show login button + X/Twitter link
  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* X/Twitter link */}
        <a
          href="https://x.com/SimpleOddsApp"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: '#000000',
            border: '2px solid #94a3b8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s ease',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.5)';
            e.currentTarget.style.borderColor = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = '#94a3b8';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>

        {/* Login button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            // On mobile, collapse the about section if provided
            if (isMobile && onLoginClick) {
              onLoginClick();
            }

            // On desktop, find the subscription banner and scroll it into view
            if (!isMobile) {
              const banner = document.getElementById('subscription-banner');
              if (banner) {
                const targetPosition = banner.offsetTop - 70;
                window.scrollTo({
                  top: targetPosition,
                  behavior: 'smooth'
                });
              }
            }
          }}
          style={{
            background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
            color: '#f59e0b',
            border: '2px solid #f59e0b',
            borderRadius: '20px',
            padding: isMobile ? '6px 12px' : '6px 16px',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(46, 204, 113, 0.3)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 4px 12px rgba(46, 204, 113, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(46, 204, 113, 0.3)';
          }}
        >
          Login
        </button>
      </div>
    );
  }

  // On mobile, show X/Twitter link + avatar circle with settings gear overlay
  if (isMobile) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* X/Twitter link */}
        <a
          href="https://x.com/SimpleOddsApp"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: '#000000',
            border: '2px solid #94a3b8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s ease',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.5)';
            e.currentTarget.style.borderColor = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = '#94a3b8';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>

        {/* User avatar button */}
        <button
          onClick={onSettingsClick}
          style={{
            position: 'relative',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: isPastDue
              ? '#f97316'
              : hasActiveSub
                ? '#198754'
                : (isTrialOrNone ? '#000000' : '#6c757d'),
            backgroundImage: isPastDue
              ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
              : hasActiveSub
                ? 'linear-gradient(135deg, #198754 0%, #157347 100%)'
                : (isTrialOrNone ? 'linear-gradient(135deg, #000000 0%, #000000 100%)' : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'),
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            fontWeight: 'bold',
            border: '2px solid #94a3b8',
            cursor: 'pointer',
            boxShadow: isPastDue
              ? '0 2px 8px rgba(249, 115, 22, 0.4)'
              : hasActiveSub
                ? '0 2px 8px rgba(25, 135, 84, 0.4)'
                : '0 2px 8px rgba(108, 117, 125, 0.3)',
            transition: 'all 0.2s ease',
            padding: 0,
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = isPastDue
              ? '0 4px 12px rgba(249, 115, 22, 0.6)'
              : hasActiveSub
                ? '0 4px 12px rgba(25, 135, 84, 0.6)'
                : '0 4px 12px rgba(108, 117, 125, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = isPastDue
              ? '0 2px 8px rgba(249, 115, 22, 0.4)'
              : hasActiveSub
                ? '0 2px 8px rgba(25, 135, 84, 0.4)'
                : '0 2px 8px rgba(108, 117, 125, 0.3)';
          }}
        >
          {userInitial}
          {/* Settings gear or warning triangle overlay */}
          <div
            style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              backgroundColor: '#343a40',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              border: '1px solid #94a3b8',
            }}
          >
            {isPastDue ? (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="#f97316" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 20h20L12 2zm0 5l7 13H5l7-13zm-1 8v2h2v-2h-2zm0-6v4h2V9h-2z"/>
              </svg>
            ) : (
              '⚙️'
            )}
          </div>
        </button>
      </div>
    );
  }

  // Tooltip styles
  const tooltipStyle = {
    position: 'absolute',
    bottom: '-32px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#1f2937',
    color: '#f3f4f6',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.15s ease, visibility 0.15s ease',
    pointerEvents: 'none',
    zIndex: 1000,
    border: '1px solid #374151'
  };

  // Desktop: show What's New + X/Twitter link + avatar circle with settings gear overlay
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {/* What's New link */}
      <div
        style={{ position: 'relative' }}
        onMouseEnter={(e) => {
          const tooltip = e.currentTarget.querySelector('.icon-tooltip');
          if (tooltip) {
            tooltip.style.opacity = '1';
            tooltip.style.visibility = 'visible';
          }
        }}
        onMouseLeave={(e) => {
          const tooltip = e.currentTarget.querySelector('.icon-tooltip');
          if (tooltip) {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
          }
        }}
      >
        <a
          href="/whats-new"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: '#1e3a5f',
            border: '2px solid #94a3b8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s ease',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.5)';
            e.currentTarget.style.borderColor = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = '#94a3b8';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 11v2h4v-2h-4zm-2 6.61c.96.71 2.21 1.65 3.2 2.39.4-.53.8-1.07 1.2-1.6-.99-.74-2.24-1.68-3.2-2.4-.4.54-.8 1.08-1.2 1.61zM20.4 5.6c-.4-.53-.8-1.07-1.2-1.6-.99.74-2.24 1.68-3.2 2.4.4.53.8 1.07 1.2 1.6.96-.72 2.21-1.65 3.2-2.4zM4 9c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h1v4h2v-4h1l5 3V6L8 9H4zm11.5 3c0-1.33-.58-2.53-1.5-3.35v6.69c.92-.81 1.5-2.01 1.5-3.34z"/>
          </svg>
        </a>
        <span className="icon-tooltip" style={tooltipStyle}>What's New</span>
      </div>

      {/* X/Twitter link */}
      <div
        style={{ position: 'relative' }}
        onMouseEnter={(e) => {
          const tooltip = e.currentTarget.querySelector('.icon-tooltip');
          if (tooltip) {
            tooltip.style.opacity = '1';
            tooltip.style.visibility = 'visible';
          }
        }}
        onMouseLeave={(e) => {
          const tooltip = e.currentTarget.querySelector('.icon-tooltip');
          if (tooltip) {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
          }
        }}
      >
        <a
          href="https://x.com/SimpleOddsApp"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: '#000000',
            border: '2px solid #94a3b8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s ease',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.5)';
            e.currentTarget.style.borderColor = '#ffffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = '#94a3b8';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>
        <span className="icon-tooltip" style={tooltipStyle}>Follow us on X</span>
      </div>

      {/* User avatar button */}
      <div
        style={{ position: 'relative' }}
        onMouseEnter={(e) => {
          const tooltip = e.currentTarget.querySelector('.icon-tooltip');
          if (tooltip) {
            tooltip.style.opacity = '1';
            tooltip.style.visibility = 'visible';
          }
        }}
        onMouseLeave={(e) => {
          const tooltip = e.currentTarget.querySelector('.icon-tooltip');
          if (tooltip) {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
          }
        }}
      >
        <button
          onClick={onSettingsClick}
          style={{
          position: 'relative',
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          backgroundColor: isPastDue
            ? '#f97316'
            : hasActiveSub
              ? '#198754'
              : (isTrialOrNone ? '#000000' : '#6c757d'),
          backgroundImage: isPastDue
            ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
            : hasActiveSub
              ? 'linear-gradient(135deg, #198754 0%, #157347 100%)'
              : (isTrialOrNone ? 'linear-gradient(135deg, #000000 0%, #000000 100%)' : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'),
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          fontWeight: 'bold',
          border: '2px solid #94a3b8',
          cursor: 'pointer',
          boxShadow: isPastDue
            ? '0 2px 8px rgba(249, 115, 22, 0.4)'
            : hasActiveSub
              ? '0 2px 8px rgba(25, 135, 84, 0.4)'
              : '0 2px 8px rgba(108, 117, 125, 0.3)',
          transition: 'all 0.2s ease',
          padding: 0,
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.05)';
          e.target.style.boxShadow = isPastDue
            ? '0 4px 12px rgba(249, 115, 22, 0.6)'
            : hasActiveSub
              ? '0 4px 12px rgba(25, 135, 84, 0.6)'
              : '0 4px 12px rgba(108, 117, 125, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = isPastDue
            ? '0 2px 8px rgba(249, 115, 22, 0.4)'
            : hasActiveSub
              ? '0 2px 8px rgba(25, 135, 84, 0.4)'
              : '0 2px 8px rgba(108, 117, 125, 0.3)';
        }}
      >
        {userInitial}
        {/* Settings gear or warning triangle overlay */}
        <div
          style={{
            position: 'absolute',
            bottom: '-2px',
            right: '-2px',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: '#343a40',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            border: '1px solid #94a3b8',
          }}
        >
          {isPastDue ? (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="#f97316" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 20h20L12 2zm0 5l7 13H5l7-13zm-1 8v2h2v-2h-2zm0-6v4h2V9h-2z"/>
            </svg>
          ) : (
            '⚙️'
          )}
        </div>
      </button>
      <span className="icon-tooltip" style={tooltipStyle}>Settings</span>
      </div>
    </div>
  );
};

export default UserMenu;
